import { Crown } from "lucide-react";
import { useEffect, useState } from "react";
import { quizAnswerLabels } from "../quiz-labels.js";
import { levelContent } from "../public/config/quizConfig.js";
import { daysBefore, todayInMexico } from "./QuizAnalytics.js";

interface Level { level: string; leads: number; qualified: number; ctaClicks: number; avgScore: number | null; avgCompletionSeconds: number | null }
type Dimension = "annualRevenue" | "teamSize" | "channel" | "campaign" | "country" | "device";
interface Quadrants {
  timeZone: string;
  range: { from: string; to: string };
  firstLeadAt: string | null;
  levels: Level[];
  weekly: Array<{ week: string; level: string; leads: number }>;
  cross: Array<{ dimension: Dimension; key: string; level: string; leads: number; qualified: number }>;
}

/** Quiz result quadrants, from the lowest maturity to the highest (score thresholds 21 / 30 / 39). */
const LEVELS = ["zoila_yolo", "pulpo", "capitan", "aguila"] as const;
const levelColor: Record<string, string> = { zoila_yolo: "#d39b4a", pulpo: "#6f9fbe", capitan: "#2f8a64", aguila: "#1d4b5c" };
const levelScore: Record<string, string> = { zoila_yolo: "12–20 puntos", pulpo: "21–29 puntos", capitan: "30–38 puntos", aguila: "39–48 puntos" };
const title = (level: string) => levelContent[level as keyof typeof levelContent]?.title ?? level;
const number = new Intl.NumberFormat("es-MX");
const pct = (part: number, whole: number) => whole > 0 ? `${Math.round((part / whole) * 1000) / 10}%` : "—";
const answerLabels = quizAnswerLabels as Record<string, Record<string, string>>;
const dimensionTabs: Array<[Dimension, string]> = [
  ["annualRevenue", "Ingresos"], ["teamSize", "Equipo"], ["channel", "Canal"], ["campaign", "Campaña"], ["country", "País"], ["device", "Dispositivo"]
];
const keyLabel = (dimension: Dimension, key: string) =>
  dimension === "annualRevenue" || dimension === "teamSize" ? answerLabels[dimension]?.[key] ?? key : key;

export function QuizQuadrants({ onUnauthorized }: { onUnauthorized: () => void }) {
  const today = todayInMexico();
  const [range, setRange] = useState({ from: daysBefore(today, 89), to: today });
  const [result, setResult] = useState<{ key: string; data: Quadrants | null; error: string }>({ key: "", data: null, error: "" });
  const [dimension, setDimension] = useState<Dimension>("annualRevenue");
  const requestKey = `${range.from}|${range.to}`;
  const loading = result.key !== requestKey;
  const data = result.data;

  useEffect(() => {
    let cancelled = false;
    fetch(`/api/v1/admin/analytics/quadrants?from=${range.from}&to=${range.to}`)
      .then(async (response) => {
        if (response.status === 401) { onUnauthorized(); return; }
        if (!response.ok) throw new Error(String(response.status));
        const next = await response.json() as Quadrants;
        if (!cancelled) setResult({ key: requestKey, data: next, error: "" });
      })
      .catch(() => { if (!cancelled) setResult((previous) => ({ key: requestKey, data: previous.data, error: "No se pudieron cargar los cuadrantes." })); });
    return () => { cancelled = true; };
  }, [range, requestKey, onUnauthorized]);

  const firstDay = data?.firstLeadAt ? new Intl.DateTimeFormat("en-CA", { timeZone: "America/Mexico_City" }).format(new Date(data.firstLeadAt)) : null;
  const presets: Array<[string, { from: string; to: string }]> = [
    ["30 días", { from: daysBefore(today, 29), to: today }],
    ["90 días", { from: daysBefore(today, 89), to: today }],
    ["12 meses", { from: daysBefore(today, 364), to: today }],
    ...(firstDay ? [["Todo", { from: firstDay < daysBefore(today, 1099) ? daysBefore(today, 1099) : firstDay, to: today }] as [string, { from: string; to: string }]] : [])
  ];

  const levels = LEVELS.map((level) => data?.levels.find((row) => row.level === level)
    ?? { level, leads: 0, qualified: 0, ctaClicks: 0, avgScore: null, avgCompletionSeconds: null });
  const total = levels.reduce((sum, row) => sum + row.leads, 0);
  const top = [...levels].sort((a, b) => b.leads - a.leads)[0];

  return (
    <>
      <section className="page-heading">
        <div><p className="eyebrow">Quiz de escalamiento</p><h1>Cuadrantes de los leads</h1></div>
        <div className="qa-range">
          <div className="qa-presets" role="group" aria-label="Rango rápido">
            {presets.map(([label, value]) => {
              const active = value.from === range.from && value.to === range.to;
              return <button key={label} className={`button ${active ? "primary" : "secondary"}`} aria-pressed={active} onClick={() => setRange(value)}>{label}</button>;
            })}
          </div>
          <label>Desde<input type="date" value={range.from} max={range.to} onChange={(event) => event.target.value && setRange({ ...range, from: event.target.value })} /></label>
          <label>Hasta<input type="date" value={range.to} min={range.from} max={today} onChange={(event) => event.target.value && setRange({ ...range, to: event.target.value })} /></label>
        </div>
      </section>

      {result.error && !loading && <div className="error-summary" role="alert">{result.error}</div>}
      <p className="qa-note">
        Cuadrante según el puntaje del quiz (12 preguntas, 12–48 puntos), por fecha del lead en hora de Ciudad de México.
        {firstDay && ` Hay leads desde el ${new Date(`${firstDay}T12:00:00`).toLocaleDateString("es-MX", { dateStyle: "long" })}.`}
        {" "}Para ver a cada cliente con su nombre, usa Zoho: cada lead llega con su puntaje.
      </p>
      {!data && loading && <p className="qa-note">Cargando…</p>}

      {data && (
        <div aria-busy={loading}>
          {top && top.leads > 0 && (
            <div className="qd-headline">
              <Crown size={20} aria-hidden="true" />
              <p>Tu público principal es <strong>{title(top.level)}</strong>: {number.format(top.leads)} de {number.format(total)} leads ({pct(top.leads, total)}).</p>
            </div>
          )}

          <section className="qd-grid" aria-label="Leads por cuadrante">
            {levels.map((row, index) => (
              <article key={row.level} className={`qd-card ${top?.level === row.level && row.leads > 0 ? "top" : ""}`} style={{ "--qd-color": levelColor[row.level] } as React.CSSProperties}>
                <header>
                  <span className="qd-number">#{index + 1}</span>
                  <div><h2>{title(row.level)}</h2><p>{levelScore[row.level]}</p></div>
                </header>
                <strong className="qd-count">{number.format(row.leads)}</strong>
                <div className="qd-share"><span style={{ width: total > 0 ? `${(row.leads / total) * 100}%` : "0%" }} /></div>
                <p className="qd-share-label">{pct(row.leads, total)} de los leads</p>
                <dl>
                  <dt>Calificados</dt><dd>{pct(row.qualified, row.leads)}</dd>
                  <dt>Clic en su resultado</dt><dd>{pct(row.ctaClicks, row.leads)}</dd>
                  <dt>Tiempo medio</dt><dd>{row.avgCompletionSeconds == null ? "—" : `${Math.floor(row.avgCompletionSeconds / 60)} min ${row.avgCompletionSeconds % 60} s`}</dd>
                  <dt>Puntaje medio</dt><dd>{row.avgScore ?? "—"}</dd>
                </dl>
                <p className="qd-description">{levelContent[row.level as keyof typeof levelContent]?.description}</p>
              </article>
            ))}
          </section>

          <section className="qa-panel">
            <div className="section-heading"><div><p className="eyebrow">Tendencia</p><h2>Leads por semana y cuadrante</h2></div></div>
            <WeeklyChart weekly={data.weekly} />
          </section>

          <section className="qa-panel">
            <div className="section-heading"><div><p className="eyebrow">Perfil</p><h2>¿Quiénes son en cada cuadrante?</h2></div></div>
            <p className="qa-note">Cada celda: leads y su peso dentro del cuadrante (la columna suma 100%).</p>
            <div className="qa-tabs" role="tablist">
              {dimensionTabs.map(([key, label]) => <button key={key} role="tab" aria-selected={dimension === key} className={dimension === key ? "active" : ""} onClick={() => setDimension(key)}>{label}</button>)}
            </div>
            <CrossTable rows={data.cross.filter((row) => row.dimension === dimension)} dimension={dimension} levels={levels} />
          </section>
        </div>
      )}
    </>
  );
}

function WeeklyChart({ weekly }: { weekly: Quadrants["weekly"] }) {
  const weeks = [...new Set(weekly.map((row) => row.week))].sort();
  if (!weeks.length) return <p className="qa-note">Sin leads en el período.</p>;
  const byWeek = new Map(weeks.map((week) => [week, Object.fromEntries(LEVELS.map((level) => [level, 0])) as Record<string, number>]));
  for (const row of weekly) { const bucket = byWeek.get(row.week); if (bucket) bucket[row.level] = row.leads; }
  const max = Math.max(1, ...weeks.map((week) => LEVELS.reduce((sum, level) => sum + (byWeek.get(week)?.[level] ?? 0), 0)));
  const width = 960, height = 230, pad = { top: 12, right: 8, bottom: 28, left: 36 };
  const inner = { w: width - pad.left - pad.right, h: height - pad.top - pad.bottom };
  const step = inner.w / weeks.length;
  const bar = Math.max(4, Math.min(48, step * 0.7));
  const labelEvery = Math.ceil(weeks.length / 12);
  return (
    <figure className="qa-chart">
      <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Leads por semana, apilados por cuadrante">
        {[0, 0.5, 1].map((tick) => (
          <g key={tick}>
            <line x1={pad.left} x2={width - pad.right} y1={pad.top + inner.h * (1 - tick)} y2={pad.top + inner.h * (1 - tick)} className="qa-grid" />
            <text x={pad.left - 6} y={pad.top + inner.h * (1 - tick) + 4} textAnchor="end" className="qa-axis">{Math.round(max * tick)}</text>
          </g>
        ))}
        {weeks.map((week, index) => {
          let y = pad.top + inner.h;
          const x = pad.left + step * index + (step - bar) / 2;
          const counts = byWeek.get(week)!;
          return (
            <g key={week}>
              <title>{`Semana del ${week}: ${LEVELS.map((level) => `${title(level)} ${counts[level]}`).join(", ")}`}</title>
              {LEVELS.map((level) => {
                const h = (counts[level]! / max) * inner.h;
                y -= h;
                return <rect key={level} x={x} y={y} width={bar} height={h} fill={levelColor[level]} />;
              })}
              {index % labelEvery === 0 && <text x={x + bar / 2} y={height - 8} textAnchor="middle" className="qa-axis">{week.slice(5)}</text>}
            </g>
          );
        })}
      </svg>
      <figcaption className="qa-legend">
        {LEVELS.map((level) => <span key={level}><i className="qa-swatch" style={{ background: levelColor[level] }} /> {title(level)}</span>)}
      </figcaption>
    </figure>
  );
}

function CrossTable({ rows, dimension, levels }: { rows: Quadrants["cross"]; dimension: Dimension; levels: Level[] }) {
  const keys = [...new Set(rows.map((row) => row.key))]
    .map((key) => ({ key, total: rows.filter((row) => row.key === key).reduce((sum, row) => sum + row.leads, 0) }))
    .sort((a, b) => b.total - a.total).slice(0, 30);
  const cell = (key: string, level: string) => rows.find((row) => row.key === key && row.level === level);
  const levelTotal = (level: string) => levels.find((row) => row.level === level)?.leads ?? 0;
  return (
    <div className="table-wrap qa-table">
      <table>
        <thead><tr><th></th>{LEVELS.map((level) => <th key={level}>{title(level)}</th>)}<th>Total</th></tr></thead>
        <tbody>
          {keys.map(({ key, total }) => (
            <tr key={key}>
              <td>{keyLabel(dimension, key)}</td>
              {LEVELS.map((level) => {
                const value = cell(key, level)?.leads ?? 0;
                const share = levelTotal(level) > 0 ? value / levelTotal(level) : 0;
                return (
                  <td key={level} className="qd-heat" style={{ "--qd-color": levelColor[level], "--qd-alpha": String(Math.min(0.45, share * 1.1)) } as React.CSSProperties}>
                    {value ? <><strong>{number.format(value)}</strong> <span>{Math.round(share * 1000) / 10}%</span></> : "—"}
                  </td>
                );
              })}
              <td><strong>{number.format(total)}</strong></td>
            </tr>
          ))}
          {!keys.length && <tr><td colSpan={LEVELS.length + 2} className="empty">Sin leads en el período.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}
